import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { ExpenseFrequency } from '../lib/expenseMonthly.js';
import type { SyncState } from './jobsDb.js';

export type ExpensePendingOp = 'create' | 'update' | 'delete' | null;

export type LocalExpense = {
  id: string;
  name: string;
  category: string | null;
  amount_pence: number;
  frequency: ExpenseFrequency;
  notes: string | null;
  active: boolean;
  deleted_at: string | null;
  syncState: SyncState;
  pendingOp: ExpensePendingOp;
  failReason: string | null;
  updatedAt: string;
};

interface ExpensesDB extends DBSchema {
  expenses: {
    key: string;
    value: LocalExpense;
    indexes: { by_sync: SyncState };
  };
}

let dbPromise: Promise<IDBPDatabase<ExpensesDB>> | null = null;

export function normalizeExpense(raw: LocalExpense): LocalExpense {
  return {
    ...raw,
    category: raw.category ?? null,
    amount_pence: raw.amount_pence ?? 0,
    frequency: raw.frequency ?? 'monthly',
    notes: raw.notes ?? null,
    active: raw.active !== false,
    deleted_at: raw.deleted_at ?? null,
    pendingOp: raw.pendingOp ?? null,
    failReason: raw.failReason ?? null,
  };
}

export function getExpensesDb() {
  if (!dbPromise) {
    dbPromise = openDB<ExpensesDB>('terratech-expenses', 1, {
      upgrade(db) {
        const store = db.createObjectStore('expenses', { keyPath: 'id' });
        store.createIndex('by_sync', 'syncState');
      },
    });
  }
  return dbPromise;
}

export async function putExpenseLocal(item: LocalExpense): Promise<void> {
  const db = await getExpensesDb();
  await db.put('expenses', normalizeExpense(item));
}

export async function getExpense(
  id: string,
): Promise<LocalExpense | undefined> {
  const db = await getExpensesDb();
  const row = await db.get('expenses', id);
  return row ? normalizeExpense(row) : undefined;
}

export async function listExpensesLocal(): Promise<LocalExpense[]> {
  const db = await getExpensesDb();
  return (await db.getAll('expenses'))
    .map(normalizeExpense)
    .filter((e) => !e.deleted_at)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export async function listExpensesBySync(
  state: SyncState,
): Promise<LocalExpense[]> {
  const db = await getExpensesDb();
  return (await db.getAllFromIndex('expenses', 'by_sync', state)).map(
    normalizeExpense,
  );
}

export async function countExpensesPending(): Promise<number> {
  const pending = await listExpensesBySync('pending');
  const syncing = await listExpensesBySync('syncing');
  return pending.length + syncing.length;
}

/** Merge server snapshot; never overwrite rows with pending local ops. */
export async function mergeExpensesFromServer(
  remote: Array<Partial<LocalExpense> & { id: string }>,
): Promise<void> {
  const db = await getExpensesDb();
  const existing = await db.getAll('expenses');
  const byId = new Map(existing.map((r) => [r.id, normalizeExpense(r)]));
  const now = new Date().toISOString();
  const remoteIds = new Set(remote.map((r) => r.id));

  for (const r of remote) {
    const local = byId.get(r.id);
    if (local && local.syncState !== 'synced' && local.pendingOp) continue;
    const next: LocalExpense = normalizeExpense({
      id: r.id,
      name: r.name ?? 'Untitled',
      category: r.category ?? null,
      amount_pence: r.amount_pence ?? 0,
      frequency: (r.frequency as ExpenseFrequency) ?? 'monthly',
      notes: r.notes ?? null,
      active: r.active !== false,
      deleted_at: null,
      syncState: 'synced',
      pendingOp: null,
      failReason: null,
      updatedAt: now,
    });
    await db.put('expenses', next);
    byId.set(r.id, next);
  }

  // Drop locally-synced rows that disappeared server-side (soft-deleted remotely).
  for (const local of byId.values()) {
    if (remoteIds.has(local.id)) continue;
    if (local.syncState !== 'synced' || local.pendingOp) continue;
    await db.delete('expenses', local.id);
  }
}

export async function deleteExpenseLocal(id: string): Promise<void> {
  const db = await getExpensesDb();
  await db.delete('expenses', id);
}

export async function __resetExpensesDbForTests(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise;
    db.close();
    dbPromise = null;
  }
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase('terratech-expenses');
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}
